import React, { useState, useEffect } from 'react';
import DeleteAccountDialog from './components/common/DeleteAccountDialog';
import { Routes, Route, Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
// MUI Imports
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import PersonOutline from '@mui/icons-material/PersonOutline';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
// Component Imports
import Login from './components/Login';
import ChatInterface from './components/ChatInterface';
import { ClientUiProvider } from './hooks/useClientUiSettings';
import { TherapistUiProvider } from './context/TherapistUiContext'; // Therapist "where am I" context for Help / Report-an-issue
import AdminDashboard from './components/admin/AdminDashboard';
import TherapistDashboard from './components/therapist/TherapistDashboard';
import TherapistPracticeSettings from './pages/therapist/TherapistPracticeSettings'; // Spec I
import TherapistPreSession from './pages/therapist/TherapistPreSession'; // Spec H
import DiagnosticAssessments from './pages/therapist/DiagnosticAssessments'; // ADHD-30..35
import TherapistSimulationLab from './pages/therapist/TherapistSimulationLab'; // Sim governance therapist entrypoint
import TherapistPracticeHistory from './pages/therapist/TherapistPracticeHistory'; // Therapist's own practice history
import InviteSignupPage from './pages/signup/InviteSignupPage'; // Therapist-led invitation landing
import ClientDetailView from './components/therapist/ClientDetailView'; // Import ClientDetailView
import TherapistLayout from './components/therapist/layout/TherapistLayout'; // Therapist sidebar shell (default chrome)
import { SIDEBAR_WIDTH, SIDEBAR_RAIL_WIDTH, TOPBAR_HEIGHT } from './components/therapist/layout/navConfig';
import CommandPalette from './components/therapist/CommandPalette'; // Ticket 2.4 — ⌘K palette
import ClinicalSupervisorHeader from './components/clinical-supervisor/ClinicalSupervisorHeader'; // Import Clinical Supervisor Header
import SupervisorConsultationView from './components/supervisor/SupervisorConsultationView'; // Import SupervisorConsultationView
import ClinicalSupervisorDashboard from './components/clinical-supervisor/ClinicalSupervisorDashboard'; // Import Clinical Supervisor Dashboard
import ResearcherDashboard from './components/researcher/ResearcherDashboard'; // Import Researcher Dashboard
import TherapistListView from './components/clinical-supervisor/TherapistListView'; // Import Therapist List
import TherapistDetailView from './components/clinical-supervisor/TherapistDetailView'; // Import Therapist Detail View
import SupervisorSimulationsView from './components/clinical-supervisor/SupervisorSimulationsView'; // Team-wide simulations overview
import SupervisorPracticeSessionsView from './components/clinical-supervisor/SupervisorPracticeSessionsView'; // Practice sessions queue
import SupervisorClientView from './components/clinical-supervisor/SupervisorClientView'; // Import Supervisor Client View
import CareNoteReviewView from './components/clinical-supervisor/CareNoteReviewView'; // Import Care Note Review
import ReportsView from './components/clinical-supervisor/ReportsView'; // Import Reports View
import ActivityLogView from './components/clinical-supervisor/ActivityLogView'; // Import Activity Log View
import TriageReviewView from './components/clinical-supervisor/TriageReviewView'; // Import Triage Review View
import AlertsView from './components/clinical-supervisor/AlertsView'; // Import Alerts View
import ConsultationDetailView from './components/clinical-supervisor/ConsultationDetailView'; // Import Consultation Detail View
import ClientManagementView from './components/clinical-supervisor/ClientManagementView'; // Import Client Management View
import ClientReviewsView from './components/clinical-supervisor/ClientReviewsView'; // Import Client Reviews View
import SupervisorTimesOfService from './components/clinical-supervisor/SupervisorTimesOfService'; // Import Supervisor Times of Service
import SupervisorQaDashboard from './components/clinical-supervisor/SupervisorQaDashboard'; // Spec F + G — QA rubric review
import BoundaryMonitoringDashboard from './components/admin/BoundaryMonitoring/BoundaryMonitoringDashboard'; // Import Boundary Monitoring Dashboard
import AuthRedirector from './components/AuthRedirector';
// Training Platform imports
import TrainingLayout from './pages/training/TrainingLayout';
import StudentDashboard from './pages/training/StudentDashboard';
import ScenarioLibrary from './pages/training/ScenarioLibrary';
import TrainingSessionPage from './pages/training/TrainingSessionPage';
import FormulationPage from './pages/training/FormulationPage';
import SelfReflectionPage from './pages/training/SelfReflectionPage';
import FeedbackPage from './pages/training/FeedbackPage';
import SessionHistory from './pages/training/SessionHistory';
import LecturerDashboard from './pages/training/LecturerDashboard';
import LecturerSessionReview from './pages/training/LecturerSessionReview';
import ScenarioWizardPage from './pages/training/ScenarioWizardPage';
import ScenarioEditorPage from './pages/training/ScenarioEditorPage';
import TrainingAdminDashboard from './pages/training/TrainingAdminDashboard';
import CohortDetailPage from './pages/training/CohortDetailPage';
import TrainingJoinPage from './pages/training/TrainingJoinPage';
import JournalManager from './components/journal/JournalManager'; // Import Journal Manager
import BookAppointment from './components/client/BookAppointment';
import WaitingRoomPage from './pages/call/WaitingRoomPage'; // Waiting room for video appointments (ST-20260915-0005)
import ScheduleOversight from './components/shared/ScheduleOversight';
import JournalEntryEditor from './components/journal/JournalEntryEditor'; // Import Journal Editor
// Parent/Teacher Portal Components
import ParentChildDashboard from './components/parent/ParentChildDashboard';
import CheckInForChild from './components/parent/CheckInForChild';
import TeacherInvitationManager from './components/parent/TeacherInvitationManager';
import TeacherPortal from './pages/TeacherPortal';
import InformantPortal from './pages/InformantPortal';
import ChangePasswordDialog from './components/ChangePasswordDialog'; // Import Change Password Dialog
// Removed OnboardingChat import
import apiService, { setAuthToken, rawApiClient } from './services/api';
import { installSessionHandling, resetSessionState } from './services/sessionManager';
import SessionTimeoutGuard from './components/common/SessionTimeoutGuard';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import MfaEnrollmentDialog from './components/auth/MfaEnrollmentDialog';

import { SocketProvider } from './context/SocketContext'; // Import SocketProvider
import CallRoomPage from './pages/call/CallRoomPage';
import IncomingCallModal from './components/call/IncomingCallModal';
import SummarySharedNotice from './components/call/SummarySharedNotice';
import MissedCallBanner from './components/call/MissedCallBanner';
import AppUpdateBanner from './components/shared/AppUpdateBanner';
import useAppUpdateAvailable from './hooks/useAppUpdateAvailable';
import UpcomingAppointmentsWidget from './components/call/UpcomingAppointmentsWidget';
import FeatureFlagDebugPanel from './components/shared/FeatureFlagDebugPanel'; // Import Feature Flag Debug Panel
import DashboardV2 from './components/v2/DashboardV2'; // Import DashboardV2
import ClientHeader from './components/v2/ClientHeader'; // Import ClientHeader
import featureFlags from './config/featureFlags'; // Import feature flags
// Therapist-Led Mode patient portal (Spec C)
import PatientPortalShell, { PortalTopBar } from './pages/portal/PatientPortalShell';
import PortalHome from './pages/portal/PortalHome';
import PortalConsent from './pages/portal/PortalConsent';
import CrisisResources from './pages/portal/CrisisResources';
import { PortalTasks, PortalSummaries, PortalDocuments, PortalSettings } from './pages/portal/PortalPlaceholders';

// Helper function to check roles
const hasRole = (userRole, roleToCheck) => {
  const roles = Array.isArray(userRole) ? userRole : [userRole];
  return roles.includes(roleToCheck);
};

// Wait for role to load before redirecting — prevents incorrect redirects on page reload
const RoleLoadingGuard = ({ token, userRole, children }) => {
  if (!token) return <Navigate to="/login" />;
  // If token exists but role hasn't loaded yet, wait instead of redirecting
  if (!userRole || (Array.isArray(userRole) && userRole.length === 0)) {
    return null; // App-level isLoading guard handles the spinner
  }
  return children;
};

// Protected Route Component for Admin
const AdminRoute = ({ token, userRole, children }) => {
  if (!token) {
    return <Navigate to="/login" />;
  }
  if (!userRole) return null;
  if (!hasRole(userRole, 'admin')) {
     const redirectPath = hasRole(userRole, 'therapist') ? "/therapist/dashboard" : "/chat";
    return <Navigate to={redirectPath} />;
  }
  return children;
};

// Protected Route Component for Therapist
const TherapistRoute = ({ token, userRole, children }) => {
  if (!token) {
    return <Navigate to="/login" />;
  }
  if (!userRole) return null;
  if (!hasRole(userRole, 'therapist')) {
     const redirectPath = hasRole(userRole, 'admin') ? "/admin/dashboard" : "/chat";
    return <Navigate to={redirectPath} />;
  }
  return children;
};

// Protected Route Component for Clinical Supervisor
const ClinicalSupervisorRoute = ({ token, userRole, children }) => {
  if (!token) {
    return <Navigate to="/login" />;
  }
  if (!userRole) return null;
  if (!hasRole(userRole, 'clinical_supervisor')) {
    const redirectPath = hasRole(userRole, 'admin') ? "/admin/dashboard" :
                        hasRole(userRole, 'therapist') ? "/therapist/dashboard" : "/chat";
    return <Navigate to={redirectPath} />;
  }
  return children;
};

// Protected Route Component for Researcher
const ResearcherRoute = ({ token, userRole, children }) => {
  if (!token) {
    return <Navigate to="/login" />;
  }
  if (!userRole) return null;
  if (!hasRole(userRole, 'researcher')) {
    const redirectPath = hasRole(userRole, 'admin') ? "/admin/dashboard" :
                        hasRole(userRole, 'clinical_supervisor') ? "/clinical-supervisor/dashboard" :
                        hasRole(userRole, 'therapist') ? "/therapist/dashboard" : "/chat";
    return <Navigate to={redirectPath} />;
  }
  return children;
};

// Protected Route Component for Training (student or lecturer role required)
const TrainingRoute = ({ token, userRole, children }) => {
  if (!token) {
    return <Navigate to="/login" />;
  }
  if (!userRole) return null;
  const hasTrainingAccess = hasRole(userRole, 'student') || hasRole(userRole, 'lecturer') || hasRole(userRole, 'training_admin') || hasRole(userRole, 'admin') || hasRole(userRole, 'therapist') || hasRole(userRole, 'clinical_supervisor');
  if (!hasTrainingAccess) {
    const redirectPath = hasRole(userRole, 'therapist') ? "/therapist/dashboard" :
                        hasRole(userRole, 'admin') ? "/admin/dashboard" : "/chat";
    return <Navigate to={redirectPath} />;
  }
  return children;
};

// Sessions (security hardening batch 2): heartbeats while the person is active, and a
// return to the login page when the server says the session has ended.
installSessionHandling({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  tokenGetter: () => { try { return localStorage.getItem('token'); } catch (_) { return null; } },
  instances: [rawApiClient]
});

function App() {
  const location = useLocation(); // Get location here
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null); // Added for platform settings
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [forcedPasswordChange, setForcedPasswordChange] = useState(false);
  // Two-step sign-in setup (security batch 4): `required` when the server insists.
  const [mfaDialog, setMfaDialog] = useState({ open: false, required: false });

  // A tab opened before a deploy keeps its bundle indefinitely: the static
  // assets are content-hashed and served immutable, so nothing re-fetches them
  // and a shipped fix can look unfixed. This notices and offers a reload.
  //
  // Called HERE, above the `if (isLoading) return` further down, because a hook
  // has to run on every render. Computing the route check inline rather than
  // reusing isOnCallRoute below, which is declared after that early return.
  //
  // Suspended during a video call: a reload drops the call, so we neither
  // prompt nor poll while someone is in one.
  const isOnCallRoute = location.pathname.startsWith('/call/');
  const updateAvailable = useAppUpdateAvailable({ enabled: !isOnCallRoute });

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch user data function
  // `silent` re-fetches without raising isLoading. The loading spinner replaces
  // the whole tree, so a non-silent refresh unmounts every page; the AIMHI
  // Direct portal refreshes the enrolment stage in the background and must not
  // do that (8 Sep 2026: it produced a remount loop after signup).
  const fetchUserDataAndSettings = async (currentToken, { silent = false } = {}) => {
    if (!currentToken) {
      setUser(null);
      setUserRole(null);
      setPlatformSettings(null); // Clear platform settings if no token
      setIsLoading(false);
      return;
    }
    if (!silent) setIsLoading(true);
    try {
      setAuthToken(currentToken); // Set token for API calls

      // Fetch user data and platform settings (now accessible to all authenticated users)
      const [userData, fetchedPlatformSettingsData] = await Promise.all([
        apiService.getUser(),
        apiService.getPlatformSettings(currentToken)
      ]);
    
      setUser(userData);
      setUserRole(userData.role);
      setPlatformSettings(fetchedPlatformSettingsData); 
      
    } catch (error) { 
      // This catch handles errors from either getUser or getPlatformSettings
      console.error("Failed to fetch user data and/or platform settings:", error);
      localStorage.removeItem('token');
      setToken(null); 
      setAuthToken(null); 
      setUser(null);
      setUserRole(null);
      setPlatformSettings(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh user data (e.g., after onboarding completes)
  const refreshUserData = async ({ silent = false } = {}) => {
    if (token) {
      await fetchUserDataAndSettings(token, { silent: silent === true });
    }
  };

  // Check for token on initial load
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      // Fetch user data will be triggered by the token state change below
    } else {
      setIsLoading(false); // No token, not loading
    }
  }, []);

  // Fetch user data whenever token changes (and is not null)
  useEffect(() => {
    if (token) {
      fetchUserDataAndSettings(token); // Use the combined fetch function
    } else {
      // If token becomes null (logout), ensure user state is cleared and not loading
      setUser(null);
      setUserRole(null);
      setPlatformSettings(null); // Clear platform settings on logout
      setIsLoading(false);
    }
  }, [token]); // Re-run only when token state changes

  // Function to update token state and store in localStorage
  const handleSetToken = (newToken, options = {}) => {
    setToken(newToken); // This triggers the useEffect above
    if (newToken) {
      localStorage.setItem('token', newToken);
      resetSessionState();
      // If forcePasswordChange flag is set, open the change password dialog
      if (options.mfaEnrollmentRequired && !options.forcePasswordChange) {
        setTimeout(() => setMfaDialog({ open: true, required: true }), 500);
      }
      if (options.forcePasswordChange) {
        // Set the forced password change flag
        setForcedPasswordChange(true);
        // Give a small delay to ensure auth state is fully updated
        setTimeout(() => setChangePasswordOpen(true), 500);
      }
    } else {
      localStorage.removeItem('token');
      // Reset forced password change flag when logging out
      setForcedPasswordChange(false);
    }
  };

  // The server ended the session (idle, expired, revoked, account changed): drop the
  // token and return to the login page, which explains why.
  useEffect(() => {
    const onEnded = () => handleSetToken(null);
    // Server-side "must change password" (security batch 3): open the forced dialog.
    const onMustChange = () => { setForcedPasswordChange(true); setChangePasswordOpen(true); };
    const onMfaRequired = () => setMfaDialog({ open: true, required: true });
    window.addEventListener('aimhi:mfa-enrollment-required', onMfaRequired);
    window.addEventListener('aimhi:session-ended', onEnded);
    window.addEventListener('aimhi:password-change-required', onMustChange);
    return () => {
      window.removeEventListener('aimhi:session-ended', onEnded);
      window.removeEventListener('aimhi:password-change-required', onMustChange);
      window.removeEventListener('aimhi:mfa-enrollment-required', onMfaRequired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => { // Make async
    if (token) { // Only call if token exists
      try {
        await apiService.logout(token); // Call the API to log the event
      } catch (err) {
        // Error is already logged by apiService.logout, client-side logout proceeds
      }
    }
    handleSetToken(null); // Clear token locally and update state
  };

  // Opens the delete dialog, which also asks (optionally) why they are leaving.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const handleDeleteAccount = () => setDeleteDialogOpen(true);
  const confirmDeleteAccount = async (reason) => {
    await apiService.deleteAccount(token, reason); // throws a readable message on failure
    setDeleteDialogOpen(false);
    handleLogout();
  };

  // Show loading indicator ONLY while actively fetching user data after token is set
  // or while initially checking for the token.
  // Let the main return handle the case where there's no token.
  if (isLoading) {
     return <div>Loading...</div>; // Keep showing loading until fetchUserData completes
  }

   // Helper to get page title based on location
   const getPageTitle = (pathname, user) => {
    const platformPrefix = 'AIMHI Platform';
    if (pathname.startsWith('/admin/dashboard')) return `${platformPrefix}: Admin Dashboard`;
    if (pathname.startsWith('/therapist/dashboard')) return `${platformPrefix}: Therapist Dashboard`;
    if (pathname.startsWith('/therapist/client/')) {
      // Placeholder for client name - this would ideally come from a shared state or context if needed in App.js
      // For now, using a generic name. This part of the title might be better handled within ClientDetailView itself
      // if the client's name is only easily available there.
      const clientName = user?.selectedClientName || 'Client'; 
      return `${platformPrefix}: Client View (${clientName})`;
    }
    if (pathname.startsWith('/chat')) return `${platformPrefix}: Client Chat`;
    if (pathname.startsWith('/journal')) return `${platformPrefix}: My Journal`;
    if (pathname.startsWith('/summaries')) return `${platformPrefix}: Session Summaries`;
    return platformPrefix; // Default
   };

   // Pass location.pathname to getPageTitle
   const pageTitle = getPageTitle(location.pathname, user);

   // Determine which header to use based on CURRENT ROUTE (not just roles)
   const isOnAdminRoute = location.pathname.startsWith('/admin');
   const isOnClinicalSupervisorRoute = location.pathname.startsWith('/clinical-supervisor');
   const isOnResearcherRoute = location.pathname.startsWith('/researcher');
   const isOnTherapistRoute = location.pathname.startsWith('/therapist');
   const isOnClientRoute = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/chat') || location.pathname.startsWith('/journal') || location.pathname.startsWith('/summaries') || location.pathname.startsWith('/parent');
   const isOnTrainingRoute = location.pathname.startsWith('/training');
   const isOnPortalRoute = location.pathname.startsWith('/portal');

   const isPortalClient = !!token && user?.effectiveClientUIMode === 'patient_portal'
     && hasRole(userRole, 'client')
     && !['therapist', 'admin', 'clinical_supervisor', 'researcher'].some(r => hasRole(userRole, r));
   const shouldUseClientHeader = token && isOnClientRoute && hasRole(userRole, 'client') && featureFlags.NEW_DASHBOARD;
   const shouldUseClinicalSupervisorHeader = token && isOnClinicalSupervisorRoute && hasRole(userRole, 'clinical_supervisor');
   const shouldUseTherapistHeader = token && isOnTherapistRoute && hasRole(userRole, 'therapist');
   // Phase 2 UX redesign: render the grouped left-sidebar shell instead of the
   // top AppBar when the flag is on. Default OFF → behaviour identical to today.
   // The therapist UI uses the left-sidebar shell everywhere; this flag-free
   // value drives the main-content offset below.
   const useSidebarNav = shouldUseTherapistHeader;

   // Render main content structure
   return (
     <SocketProvider token={token}>
      <ClientUiProvider>
      <TherapistUiProvider>
       {/* Conditionally render appropriate header based on user role (check highest priority first) */}
       {/* Training and patient-portal routes own their layout/header — skip the default */}
       {/* A portal client gets the portal's own top bar on every page outside
           /portal as well: the call page (ST-20260925-0004), booking and the
           waiting room (ST-20260928-0001). The blue app bar is never theirs.
           effectiveClientUIMode comes from the user's service group whatever
           their role, so staff in a portal group carry it too: the bar is for
           clients only, or therapists lose their own layout (ST-20260928-0003). */}
       {isOnTrainingRoute || isOnPortalRoute ? null : isPortalClient ? (
         <PortalTopBar user={user} />
       ) : shouldUseClinicalSupervisorHeader ? (
         <ClinicalSupervisorHeader
           user={user}
           onLogout={handleLogout}
           onChangePassword={() => setChangePasswordOpen(true)}
           onDeleteAccount={handleDeleteAccount}
           pageTitle={pageTitle}
         />
       ) : shouldUseTherapistHeader ? (
         <TherapistLayout
           user={user}
           onLogout={handleLogout}
           onChangePassword={() => setChangePasswordOpen(true)}
           onDeleteAccount={handleDeleteAccount}
           pageTitle={pageTitle}
         />
       ) : shouldUseClientHeader ? (
         <ClientHeader
           user={user}
           token={token}
           onLogout={handleLogout}
           onChangePassword={() => setChangePasswordOpen(true)}
           onDeleteAccount={handleDeleteAccount}
         />
       ) : (
         <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}> {/* Fixed position */}
           <Toolbar>
           {/* Logo */}
           <RouterLink to={token ? (hasRole(userRole, 'admin') ? '/admin/dashboard' : hasRole(userRole, 'therapist') ? '/therapist/dashboard' : '/chat') : '/login'} 
                       style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: '16px' }}>
             {/* Desktop logo */}
             <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
               <img 
                 src={`${process.env.PUBLIC_URL}/logo-desktop.png`}
                 srcSet={`${process.env.PUBLIC_URL}/logo-desktop.png 1x, ${process.env.PUBLIC_URL}/logo-desktop2x.png 2x`}
                 alt="AIMHI Logo"
                 style={{ 
                   height: '40px',
                   width: 'auto'
                 }}
               />
             </Box>
             {/* Mobile logo */}
             <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
               <img 
                 src={`${process.env.PUBLIC_URL}/logo-mobile.png`}
                 srcSet={`${process.env.PUBLIC_URL}/logo-mobile.png 1x, ${process.env.PUBLIC_URL}/logo-mobile2x.png 2x`}
                 alt="AIMHI Logo"
                 style={{ 
                   height: '35px',
                   width: 'auto'
                 }}
               />
             </Box>
           </RouterLink>
           <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {pageTitle}
           </Typography>
            {/* "Logged in as" text - moved here for better layout control */}
            {token && user && (
              <Typography variant="body2" sx={{ color: 'inherit', mr: 2 }}>
                Logged in as: {user.email}
              </Typography>
            )}
            {/* Conditional Links - Only show if user is onboarded */}
            {token && user?.isOnboarded && hasRole(userRole, 'client') && (
              <>
                {featureFlags.NEW_DASHBOARD && (
                  <Button color="inherit" component={RouterLink} to="/dashboard">Dashboard</Button>
                )}
                <Button color="inherit" component={RouterLink} to="/chat">Chat</Button>
                <Button color="inherit" component={RouterLink} to="/journal">Journal</Button> 
                {/* Add Journal Link for Clients */}
              </>
            )}
            {token && user?.isOnboarded && hasRole(userRole, 'therapist') && (
             <Button color="inherit" component={RouterLink} to="/therapist/dashboard">Therapist Dashboard</Button>
           )}
           {token && user?.isOnboarded && hasRole(userRole, 'admin') && (
              <Button color="inherit" component={RouterLink} to="/admin/dashboard">Admin Dashboard</Button>
            )}
            {token && (hasRole(userRole, 'student') || hasRole(userRole, 'lecturer')) && (
              <Button color="inherit" component={RouterLink} to="/training">Training</Button>
            )}
            {/* Account Menu - Show if logged in */}
            {token && (
              <div>
                <IconButton
                 size="large"
                 aria-label="account of current user"
                 aria-controls="menu-appbar"
                 aria-haspopup="true"
                 onClick={handleMenu}
                 color="inherit"
                 data-testid="user-menu-button"
               >
                 <AccountCircle />
               </IconButton>
               <Menu
                 id="menu-appbar"
                 anchorEl={anchorEl}
                 anchorOrigin={{
                   vertical: 'top',
                   horizontal: 'right',
                 }}
                 keepMounted
                 transformOrigin={{
                   vertical: 'top',
                   horizontal: 'right',
                 }}
                 open={Boolean(anchorEl)}
                 onClose={handleClose}
               >
                 {/* Logged in as text is now outside the menu, directly in Toolbar */}
                 <MenuItem onClick={() => { handleClose(); setChangePasswordOpen(true); }}>Change Password</MenuItem>

                 {/* Role Switching Options */}
                 {token && user && (
                   <>
                     {(hasRole(userRole, 'clinical_supervisor') || hasRole(userRole, 'therapist') || hasRole(userRole, 'admin') || hasRole(userRole, 'researcher')) && (
                       <>
                         <Divider />
                         {hasRole(userRole, 'clinical_supervisor') && !isOnClinicalSupervisorRoute && (
                           <MenuItem
                             component={RouterLink}
                             to="/clinical-supervisor/dashboard"
                             onClick={handleClose}
                           >
                             <ListItemIcon>
                               <SwapHoriz fontSize="small" />
                             </ListItemIcon>
                             Switch to Clinical Supervisor View
                           </MenuItem>
                         )}
                         {hasRole(userRole, 'researcher') && !isOnResearcherRoute && (
                           <MenuItem
                             component={RouterLink}
                             to="/researcher/dashboard"
                             onClick={handleClose}
                           >
                             <ListItemIcon>
                               <SwapHoriz fontSize="small" />
                             </ListItemIcon>
                             Switch to Researcher View
                           </MenuItem>
                         )}
                         {hasRole(userRole, 'therapist') && !isOnTherapistRoute && (
                           <MenuItem
                             component={RouterLink}
                             to="/therapist/dashboard"
                             onClick={handleClose}
                           >
                             <ListItemIcon>
                               <SwapHoriz fontSize="small" />
                             </ListItemIcon>
                             Switch to Therapist View
                           </MenuItem>
                         )}
                         {hasRole(userRole, 'admin') && !isOnAdminRoute && (
                           <MenuItem
                             component={RouterLink}
                             to="/admin/dashboard"
                             onClick={handleClose}
                           >
                             <ListItemIcon>
                               <SwapHoriz fontSize="small" />
                             </ListItemIcon>
                             Switch to Admin View
                           </MenuItem>
                         )}
                       </>
                     )}
                   </>
                 )}

                 <Divider />
                 <MenuItem onClick={() => { handleLogout(); handleClose(); }} data-testid="logout-button">Logout</MenuItem>
                 <Divider />
                 <MenuItem
                    onClick={() => { handleDeleteAccount(); handleClose(); }}
                    sx={{ color: 'error.main' }}
                 >
                    Delete Account
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
         </AppBar>
       )}
       {/* Ticket 2.4 — ⌘K command palette, available across all therapist routes (flag-independent) */}
       {shouldUseTherapistHeader && <CommandPalette token={token} />}

       {/* Outside the Container so it reaches every route, including the portal
           and training, which draw their own layouts: a stale bundle affects
           everyone equally. Never shown during a call — a reload drops it. */}
       {!isOnCallRoute && <AppUpdateBanner available={updateAvailable} />}

       {/* Main Content Area - Only render routes if NOT loading */}
       <Container component="main" maxWidth={(isOnTrainingRoute || isOnPortalRoute) ? false : (useSidebarNav ? false : "lg")} disableGutters={isOnTrainingRoute || isOnPortalRoute} sx={{ mt: (isOnTrainingRoute || isOnPortalRoute) ? 0 : (useSidebarNav ? `${TOPBAR_HEIGHT + 16}px` : 10), mb: (isOnTrainingRoute || isOnPortalRoute) ? 0 : 4, px: (isOnTrainingRoute || isOnPortalRoute) ? 0 : undefined, ...(useSidebarNav ? { ml: { xs: 0, md: `${SIDEBAR_RAIL_WIDTH}px`, lg: `${SIDEBAR_WIDTH}px` }, width: { xs: '100%', md: `calc(100% - ${SIDEBAR_RAIL_WIDTH}px)`, lg: `calc(100% - ${SIDEBAR_WIDTH}px)` }, boxSizing: 'border-box' } : {}) }}>
         {/* At the top of the page content. Rendered after the routes it only
             appeared once the client scrolled to the bottom (ST-20260911-0004).
             The patient portal draws its own, inside its content area: its
             header is fixed, so a banner here sat underneath it. */}
         {token && user && !isOnPortalRoute && <MissedCallBanner currentUser={user} token={token} />}
         <Routes>
           <Route
             path="/login"
             // If token exists, redirect to the AuthRedirector to handle role-based navigation
             // Otherwise, show the Login component
             element={!token ? <Login setToken={handleSetToken} /> : <Navigate to="/auth/redirect" replace />}
           />
           {/* Forgot-password link from the email (security batch 3) */}
           <Route path="/reset-password" element={<ResetPasswordPage />} />
           {/* Signup route with code as query parameter — fallback to Login */}
           <Route
             path="/signup"
             element={!token ? <Login setToken={handleSetToken} /> : <Navigate to="/auth/redirect" replace />}
           />
           {/* Therapist-led invitation landing — therapist photo + clinic branding.
               Reads :invite from path; falls back to legacy Login if invite invalid. */}
           <Route
             path="/signup/:invite"
             element={!token ? <InviteSignupPage setToken={handleSetToken} /> : <Navigate to="/auth/redirect" replace />}
           />
           {/* Therapist-Led patient portal (Spec C) — gated by user.effectiveClientUIMode === 'patient_portal' */}
           <Route
             path="/portal"
             element={
               token
                 ? <PatientPortalShell user={user} onLogout={handleLogout} onRefreshUser={() => refreshUserData({ silent: true })} />
                 : <Navigate to="/login" replace />
             }
           >
             <Route index element={<PortalHome />} />
             <Route path="consent" element={<PortalConsent />} />
             <Route path="tasks" element={<PortalTasks />} />
             <Route path="summaries" element={<PortalSummaries />} />
             <Route path="documents" element={<PortalDocuments />} />
             <Route path="crisis" element={<CrisisResources />} />
             <Route path="settings" element={<PortalSettings />} />
           </Route>
           {/* Dashboard Route (v2) - Only available when feature flag is enabled.
               Therapist-Led / hybrid clients have no AI dashboard — bounce to /portal. */}
           {featureFlags.NEW_DASHBOARD && (
             <Route
               path="/dashboard"
               element={
                 !token || !hasRole(userRole, 'client')
                   ? <Navigate to="/login" replace />
                   : (user?.effectiveClientUIMode === 'patient_portal'
                       ? <Navigate to="/portal" replace />
                       : <DashboardV2 token={token} user={user} refreshUserData={refreshUserData} />)
               }
             />
           )}
           <Route
             path="/chat"
             // Protect chat route. Therapist-Led / hybrid clients (whose
             // effectiveClientUIMode is 'patient_portal') don't have an AI
             // chat surface — bounce them to /portal so direct URL access /
             // any legacy redirect can't drop them into the AI onboarding flow.
             element={
               !token
                 ? <Navigate to="/login" replace />
                 : (user?.effectiveClientUIMode === 'patient_portal'
                     ? <Navigate to="/portal" replace />
                     : <ChatInterface token={token} user={user} defaultVoiceId={platformSettings?.defaultElevenLabsVoiceId} refreshUserData={refreshUserData} />)
             }
           />
           {/* Intermediate route for handling redirection after login */}
           <Route
             path="/auth/redirect"
             element={
               token ? (
                 <AuthRedirector isLoading={isLoading} user={user} userRole={userRole} />
               ) : (
                 <Navigate to="/login" replace /> // If somehow landed here without token, go to login
               )
             }
           />
           {/* Admin Route - Allow sub-paths */}
           <Route
             path="/admin/dashboard/*"
             element={
               <AdminRoute token={token} userRole={userRole}>
                 <AdminDashboard token={token} userRole={userRole} />
               </AdminRoute>
             }
           />
           {/* Therapist Route */}
           <Route
             path="/therapist/dashboard"
             element={
               <TherapistRoute token={token} userRole={userRole}>
                 {/* Let TherapistDashboard handle checks if needed */}
                  <TherapistDashboard token={token} user={user} />
                </TherapistRoute>
              }
            />
            {/* Therapist Practice Settings (Spec I) */}
            <Route
              path="/therapist/practice-settings"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <TherapistPracticeSettings />
                </TherapistRoute>
              }
            />
            {/* Therapist Pre-Session Workflow (Spec H) */}
            <Route
              path="/therapist/pre-session"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <TherapistPreSession />
                </TherapistRoute>
              }
            />
            <Route
              path="/therapist/diagnostic"
              element={<TherapistRoute token={token} userRole={userRole}><DiagnosticAssessments /></TherapistRoute>}
            />
            <Route
              path="/therapist/diagnostic/:id"
              element={<TherapistRoute token={token} userRole={userRole}><DiagnosticAssessments /></TherapistRoute>}
            />
            {/* Therapist Simulation Lab (sim governance) */}
            <Route
              path="/therapist/simulation-lab"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <TherapistSimulationLab />
                </TherapistRoute>
              }
            />
            <Route
              path="/therapist/simulation-lab/history"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <TherapistPracticeHistory />
                </TherapistRoute>
              }
            />
            {/* Therapist Client Detail Route */}
            <Route
              path="/therapist/client/:clientId"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <ClientDetailView />
                  {/* Pass token if needed by ClientDetailView, e.g., <ClientDetailView token={token} /> */}
                </TherapistRoute>
              }
            />
            {/* Supervisor Consultation Routes */}
            <Route
              path="/supervisor/consultation/new/:clientId/:conversationId"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <SupervisorConsultationView token={token} />
                </TherapistRoute>
              }
            />
            <Route
              path="/supervisor/consultation/:consultationId"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <SupervisorConsultationView token={token} />
                </TherapistRoute>
              }
            />
            {/* Therapist Client Reviews Route */}
            <Route
              path="/therapist/reviews"
              element={
                <TherapistRoute token={token} userRole={userRole}>
                  <ClientReviewsView token={token} userRole={userRole} />
                </TherapistRoute>
              }
            />
            {/* Clinical Supervisor Routes */}
            <Route
              path="/clinical-supervisor/dashboard"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ClinicalSupervisorDashboard token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            {/* Slots and bookings across supervised / same-group therapists (AD-04, D-16) */}
            <Route
              path="/clinical-supervisor/schedules"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ScheduleOversight token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/therapists"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <TherapistListView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/therapists/:therapistId"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <TherapistDetailView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/therapists/:therapistId/clients/:clientId"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <SupervisorClientView token={token} user={user} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/simulations"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <SupervisorSimulationsView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/practice-sessions"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <SupervisorPracticeSessionsView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/care-notes/pending"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <CareNoteReviewView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/reviews"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ClientReviewsView token={token} userRole={userRole} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/alerts"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <AlertsView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/reports"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ReportsView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/activity"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ActivityLogView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/triage"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <TriageReviewView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            {/* Spec F + G — QA Rubric Review */}
            <Route
              path="/clinical-supervisor/qa"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <SupervisorQaDashboard token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/consultations/:consultationId"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ConsultationDetailView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/clients"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <ClientManagementView token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/service-hours"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <SupervisorTimesOfService token={token} />
                </ClinicalSupervisorRoute>
              }
            />
            <Route
              path="/clinical-supervisor/boundary-monitoring"
              element={
                <ClinicalSupervisorRoute token={token} userRole={userRole}>
                  <BoundaryMonitoringDashboard token={token} userRole={userRole} />
                </ClinicalSupervisorRoute>
              }
            />
            {/* Researcher Routes */}
            <Route
              path="/researcher/dashboard/*"
              element={
                <ResearcherRoute token={token} userRole={userRole}>
                  <ResearcherDashboard token={token} user={user} />
                </ResearcherRoute>
              }
            />
            {/* Appointment self-booking (AIMHI Direct AD-04). Clients claim an
                intake or exit slot; claiming assigns them to that therapist. */}
            <Route
              path="/appointments/book"
              element={token ? <BookAppointment token={token} slotType="intake" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/appointments/book/exit"
              element={token ? <BookAppointment token={token} slotType="exit" /> : <Navigate to="/login" replace />}
            />
            {/* Waiting room for a video appointment (ST-20260915-0005). */}
            <Route
              path="/appointments/:appointmentId/waiting"
              element={token ? <WaitingRoomPage token={token} /> : <Navigate to="/login" replace />}
            />

            {/* Session summaries a therapist has shared, for clients past the waiting
                area (ST-20260918-0001). The waiting area has the same page at /portal/summaries. */}
            <Route
              path="/summaries"
              element={token ? <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 3 }}><PortalSummaries /></Box> : <Navigate to="/login" replace />}
            />

            {/* Journal Routes (Protected) */}
            <Route
              path="/journal"
              element={token ? <JournalManager token={token} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/journal/new"
              element={token ? <JournalEntryEditor token={token} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/journal/entry/:entryId"
              element={token ? <JournalEntryEditor token={token} /> : <Navigate to="/login" replace />}
            />
            {/* Training Platform Routes */}
            <Route
              path="/training"
              element={
                <TrainingRoute token={token} userRole={userRole}>
                  <TrainingLayout token={token} user={user} onLogout={handleLogout} />
                </TrainingRoute>
              }
            >
              <Route index element={<StudentDashboard />} />
              <Route path="sessions" element={<SessionHistory />} />
              <Route path="scenarios" element={<ScenarioLibrary />} />
              <Route path="session/:id" element={<TrainingSessionPage />} />
              <Route path="session/:id/formulation" element={<FormulationPage />} />
              <Route path="session/:id/reflection" element={<SelfReflectionPage />} />
              <Route path="session/:id/feedback" element={<FeedbackPage />} />
              <Route path="lecturer" element={<LecturerDashboard />} />
              <Route path="lecturer/session/:id" element={<LecturerSessionReview />} />
              <Route path="scenarios/wizard" element={<ScenarioWizardPage />} />
              <Route path="scenarios/new" element={<ScenarioEditorPage />} />
              <Route path="scenarios/:id/edit" element={<ScenarioEditorPage />} />
              <Route path="admin" element={<TrainingAdminDashboard />} />
              <Route path="admin/cohorts/:id" element={<CohortDetailPage />} />
              <Route path="join" element={<TrainingJoinPage />} />
            </Route>
            {/* Parent Routes (for users with parent_of relationships) */}
            <Route
              path="/parent/dashboard"
              element={token ? <ParentChildDashboard token={token} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/parent/child/:childId/checkin"
              element={token ? <CheckInForChild token={token} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/parent/child/:childId/teachers"
              element={token ? <TeacherInvitationManager token={token} /> : <Navigate to="/login" replace />}
            />
            {/* Informant portal (public, private link; ADHD-21) */}
            <Route
              path="/informant/:token"
              element={<InformantPortal />}
            />
            {/* Teacher Portal (Public - no auth required) */}
            <Route
              path="/teacher-portal/:token"
              element={<TeacherPortal />}
            />
            {/* Live video calls */}
            <Route
              path="/call/:liveCallId"
              element={token ? <CallRoomPage token={token} user={user} /> : <Navigate to="/login" replace />}
            />
            {/* Default route - Redirect based on token */}
            <Route
              path="*" // Default route: if token exists, go to redirector, else login
              element={<Navigate to={token ? "/auth/redirect" : "/login"} replace />}
            />
          </Routes>
        </Container>
       {/* Change Password Dialog */}
       <ChangePasswordDialog 
         open={changePasswordOpen} 
         onClose={() => {
           setChangePasswordOpen(false);
           setForcedPasswordChange(false);
         }} 
         token={token}
         forcedChange={forcedPasswordChange}
         onOpenMfa={() => { setChangePasswordOpen(false); setMfaDialog({ open: true, required: false }); }}
       />
       {token && (
         <MfaEnrollmentDialog
           open={mfaDialog.open}
           required={mfaDialog.required}
           onClose={() => setMfaDialog({ open: false, required: false })}
           onEnabled={() => refreshUserData({ silent: true })}
         />
       )}
       {token && user && <SessionTimeoutGuard onSignOut={handleLogout} />}
       {/* Feature Flag Debug Panel - Only shows in development */}
       <FeatureFlagDebugPanel />
       {/* Globally listens for incoming video call invitations (clients only) */}
       {token && user && <IncomingCallModal currentUser={user} token={token} />}
       {token && user && <SummarySharedNotice currentUser={user} />}
       {/* Client dashboard upcoming appointments sticky banner */}
       {token && user && hasRole(userRole, 'client') && isOnClientRoute && (
         <Box sx={{ position: 'sticky', top: 64, zIndex: 1100, mx: 2, mt: 2 }}>
           <UpcomingAppointmentsWidget token={token} role="client" />
         </Box>
       )}
      </TherapistUiProvider>
      </ClientUiProvider>
        <DeleteAccountDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDeleteAccount}
        />
      </SocketProvider>
   );
 }

export default App;
